import { useCallback, useEffect, useState } from "react";
import { Download, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { api, errMsg } from "../lib/api";

const STATUSES = ["New", "Contacted", "Qualified", "Booked", "Lost"];
const STYLE = { New: "bg-[#FFF3EC] text-[#FD691B]", Contacted: "bg-blue-100 text-blue-800", Qualified: "bg-violet-100 text-violet-800", Booked: "bg-emerald-100 text-emerald-800", Lost: "bg-slate-100 text-slate-600" };
const fmt = (d) => new Date(d).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });

const Row = ({ k, v }) => v ? <div className="grid grid-cols-[120px_1fr] gap-2 text-sm"><dt className="text-slate-500">{k}</dt><dd className="break-words font-medium">{v}</dd></div> : null;

const Detail = ({ inq, onChange, onDelete }) => {
  const [note, setNote] = useState("");
  const patch = async (body) => {
    try { const { data } = await api.patch(`/admin/inquiries/${inq.id}`, body); onChange(data); toast.success("Inquiry updated"); setNote(""); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mt-6 space-y-6" data-testid="inquiry-detail">
      <dl className="space-y-2">
        <Row k="Received" v={fmt(inq.created_at)} /><Row k="Phone" v={inq.phone} /><Row k="Email" v={inq.email} /><Row k="Tour" v={inq.tour_title} />
        <Row k="Destination" v={inq.destination} /><Row k="Travel month" v={inq.travel_month} /><Row k="Travel date" v={inq.travel_date} /><Row k="Travelers" v={String(inq.travelers)} />
        <Row k="Category" v={inq.category} /><Row k="Source" v={inq.source} /><Row k="Message" v={inq.message} />
      </dl>
      <div><label className="label">Lead status</label>
        <select className="field" value={inq.status} onChange={(e) => patch({ status: e.target.value })} data-testid="inquiry-status-select">{STATUSES.map((s) => <option key={s}>{s}</option>)}</select></div>
      <div><label className="label">Internal notes (private)</label>
        <ul className="mb-3 space-y-2">{(inq.notes || []).map((n, i) => <li key={i} className="rounded-xl bg-slate-50 p-3 text-sm" data-testid={`inquiry-note-${i}`}>{n.text}<span className="mt-1 block text-xs text-slate-400">{n.by} · {fmt(n.at)}</span></li>)}</ul>
        <textarea className="field" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note" data-testid="inquiry-note-input" />
        <button onClick={() => patch({ note })} disabled={!note.trim()} className="btn-brand mt-2 !py-2.5 disabled:opacity-50" data-testid="inquiry-add-note-btn">Add note</button></div>
      <button onClick={onDelete} className="flex items-center gap-2 text-sm font-semibold text-red-600" data-testid="inquiry-delete-btn"><Trash2 className="h-4 w-4" />Delete inquiry</button>
    </div>
  );
};

export default function AdminInquiries() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (status) p.set("status", status);
    if (q.trim()) p.set("q", q.trim());
    api.get(`/admin/inquiries?${p}`).then((r) => setRows(r.data));
  }, [status, q]);
  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const exportCsv = async () => {
    try {
      const { data } = await api.get("/admin/inquiries-export", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = "wandervibe-inquiries.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const update = (d) => { setSel(d); setRows((r) => r.map((x) => (x.id === d.id ? d : x))); };
  const remove = async () => {
    if (!window.confirm("Delete this inquiry?")) return;
    await api.delete(`/admin/inquiries/${sel.id}`);
    setSel(null); load(); toast.success("Inquiry deleted");
  };

  return (
    <div data-testid="admin-inquiries-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl sm:text-3xl">Inquiries</h1>
        <button onClick={exportCsv} className="btn-brand" data-testid="admin-export-csv-btn"><Download className="h-4 w-4" />Export CSV</button>
      </div>
      <div className="mt-6 flex flex-wrap gap-3">
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="relative min-w-[220px] flex-1"><Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input className="field !pl-11" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, phone, tour" data-testid="inquiries-search-input" /></form>
        <select className="field max-w-[200px]" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="inquiries-status-filter"><option value="">All statuses</option>{STATUSES.map((s) => <option key={s}>{s}</option>)}</select>
      </div>
      <div className="mt-6 overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500"><tr><th className="p-4">Received</th><th className="p-4">Name</th><th className="p-4">Contact</th><th className="p-4">Tour / destination</th><th className="p-4">Travelers</th><th className="p-4">Status</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} onClick={() => setSel(r)} className="cursor-pointer border-t border-slate-100 hover:bg-slate-50" data-testid={`inquiry-row-${r.id}`}>
                <td className="p-4 text-xs text-slate-500">{fmt(r.created_at)}</td><td className="p-4 font-semibold">{r.name}</td>
                <td className="p-4 text-xs">{r.phone}<br />{r.email}</td><td className="p-4">{r.tour_title || r.destination || "—"}</td><td className="p-4">{r.travelers}</td>
                <td className="p-4"><span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${STYLE[r.status]}`}>{r.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
        {!rows.length && <p className="p-10 text-center text-sm text-slate-500" data-testid="inquiries-empty">No inquiries yet.</p>}
      </div>
      <Sheet open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-md">
          <SheetHeader><SheetTitle className="font-display">{sel?.name}</SheetTitle></SheetHeader>
          {sel && <Detail key={sel.id} inq={sel} onChange={update} onDelete={remove} />}
        </SheetContent>
      </Sheet>
    </div>
  );
}
